import React, { useState, useContext, useEffect } from 'react';
import { Link } from 'gatsby';
import { IoIosArrowForward } from 'react-icons/io';
import { LocaleContext } from '../layout';
import Icon from '../Icon';

import locales from '../../../config/i18n';
import { getLanguageTargets } from '../../utils/language-targets';

import * as styles from './LangSwitcher.module.scss';

// Lang switcher avec la logique d'origine (survol + toggle), styles via module SCSS
export function LangSwitcher({ flags, onSelect, onRegisterClose, onToggle }) {
  const localeContext = useContext(LocaleContext);
  const [isOpen, setIsOpen] = useState(false);
  const [canHover, setCanHover] = useState(false);
  const iconsMap = {
    left: IoIosArrowForward,
  };
  const handleSelect = () => {
    setIsOpen(false);
    if (onSelect) onSelect();
  };

  const handleToggle = () => {
    setIsOpen((prev) => {
      const next = !prev;
      if (onToggle) onToggle(next);
      return next;
    });
  };

  useEffect(() => {
    if (onRegisterClose) {
      onRegisterClose(() => setIsOpen(false));
    }
  }, [onRegisterClose]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const media = window.matchMedia('(hover: hover) and (pointer: fine)');
    const update = () => setCanHover(media.matches);
    update();
    if (media.addEventListener) {
      media.addEventListener('change', update);
    } else {
      media.addListener(update);
    }
    return () => {
      if (media.removeEventListener) {
        media.removeEventListener('change', update);
      } else {
        media.removeListener(update);
      }
    };
  }, []);

  const handleMouseEnter = () => {
    if (!canHover) return;
    setIsOpen(true);
    if (onToggle) onToggle(true);
  };

  const handleMouseLeave = () => {
    if (!canHover) return;
    setIsOpen(false);
    if (onToggle) onToggle(false);
  };

  if (!localeContext) return null;

  let { locale, location, alternateLanguages, availablePaths } = localeContext;
  locale = locale || 'en-gb';

  const targets = getLanguageTargets({
    locale,
    pathname: location.pathname,
    alternateLanguages,
    availablePaths,
  });
  if (!targets.length) return null;

  const renderFlags = (lang, isDecorative = false) =>
    flags?.nodes
      ?.filter((node) => node.name === `flag-${lang}`)
      .map((loc) => (
        <img
          loading="lazy"
          decoding="async"
          key={lang}
          src={loc.publicURL}
          alt={isDecorative ? '' : locales[lang].langName}
          aria-hidden={isDecorative}
          className={styles.langSwitcher__flag}
        />
      ));

  return (
    <div
      className={styles.langSwitcher}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        type="button"
        onClick={handleToggle}
        className={`${styles.langSwitcher__button} ${
          isOpen ? styles.langSwitcher__buttonOpen : ''
        }`}
      >
        {renderFlags(locale)}
        <Icon
          name="left"
          iconsMap={iconsMap}
          className={`${styles.langSwitcher__icon} ${
            isOpen ? styles.langSwitcher__iconOpen : ''
          }`}
        />
      </button>

      <div
        className={styles.langSwitcher__menu}
        style={{ display: isOpen ? 'flex' : 'none' }}
        role="menu"
        aria-hidden={!isOpen}
      >
        {targets.map(({ lang, to }) => (
          <Link
            key={lang}
            to={to}
            onClick={handleSelect}
            className={styles.langSwitcher__item}
          >
            {renderFlags(lang, true)}
            {locales[lang].langName}
          </Link>
        ))}
      </div>
    </div>
  );
}
